import { useState, type ReactNode } from 'react';
import { useT } from '@hana/i18n';
import './bookingI18n';
import {
  IconAlertCircle, IconChecklist, IconChevronDown, IconChevronLeft, IconClockHour4, IconCopy, IconDots, IconInfoCircle, IconLink, IconMapRoute, IconMessageCircle, IconSend,
  IconShip, IconUsers, IconCalendarCheck, IconExternalLink, IconAnchor, IconBoxSeam, type Icon,
} from '@tabler/icons-react';
import { Button, Chip } from '@hana/ui';
import { PageHeader } from '@hana/shell';
import { padDetail, type Tone } from './padDetail';
import type { BookingRequest } from './types';
import s from './bookingPadDetail.module.css';

// Mint-Booking-iPad-Detail (1180): trang chi tiết 2:1 — cột trái Kế hoạch · Booking hãng · Comment nội bộ · Nhật ký job; cột phải 340px Việc cần làm ·
// Thông tin chính · Người phụ trách · Liên kết. Mỗi thẻ gập được (nút 44). Chỉ đọc: nút ghi báo toast qua `onWrite`.

export const MENU: ReadonlyArray<{ g?: string; k: string; tip: string }> = [
  { g: 'Booking', k: 'Gửi booking request cho hãng', tip: 'Thư từ info@ kèm thư khách' },
  { k: 'Nhập Pre Booking', tip: 'Ghi số pre-booking của hãng' },
  { k: 'Đánh dấu Booked', tip: 'Đã đặt, chờ hãng xác nhận' },
  { k: 'Hãng hết chỗ (No Space)', tip: 'Báo Sale chọn hãng / chuyến khác' },
  { k: 'Đặt Standby', tip: 'Giữ chỗ, chờ khách chốt' },
  { k: 'Chọn chuyến từ lịch tàu', tip: 'Theo hãng của RQ' },
  { k: 'Amend', tip: 'Sửa booking với hãng, ghi nhật ký' },
  { k: 'Manual Confirm', tip: 'Chuyển Confirmed, không gửi mail khách' },
  { k: 'Copy Booking', tip: 'Nhân bản RQ cho lô kế tiếp' },
  { g: 'Chứng từ', k: 'In H-BKG', tip: 'House booking gửi khách' },
  { k: 'Yêu cầu cấp rỗng (ROD)', tip: 'Theo cổng ROD của hãng' },
  { g: 'Rủi ro', k: 'No show', tip: 'Phí quy về Sale yêu cầu' },
  { k: 'Huỷ booking', tip: 'Ghi vào nhật ký job' },
];

const TONE_TX: Record<Tone, string> = { err: s.tErr!, warn: s.tWarn!, lb: s.tLb!, ok: s.tOk!, info: s.tInfo! };

function Card({ id, icon: Ico, title, tip, count, open, onToggle, children }: { id: string; icon: Icon; title: string; tip: string; count?: number; open: boolean; onToggle: (id: string) => void; children: ReactNode }) {
  return (
    <section aria-label={title} className={s.card}>
      <div className={s.ch}>
        <span className={s.chIc}>
          <Ico size={18} stroke={1.9} aria-hidden="true" />
        </span>
        <h2 className={s.h2}>{title}</h2>
        <span role="img" aria-label={`Giải thích: ${tip}`} title={tip} className={s.hint}>
          <IconInfoCircle size={14} stroke={1.9} aria-hidden="true" />
        </span>
        {!!count && <span className={s.cnt}>{count}</span>}
        <button type="button" className={s.tog} aria-expanded={open} aria-label={`Mở hoặc gập ${title}`} onClick={() => onToggle(id)}>
          <IconChevronDown size={18} stroke={1.9} aria-hidden="true" className={open ? s.rot : undefined} />
        </button>
      </div>
      {open && <div className={s.body}>{children}</div>}
    </section>
  );
}

function Avatar({ name, size }: { name: string; size: number }) {
  const ini = name.replace(/[^A-Za-zÀ-ỹ]/g, '').slice(0, 2).toUpperCase() || '—';
  return (
    <span className={s.av} title={name} style={{ width: size, height: size }}>
      {ini}
    </span>
  );
}

export function BookingPadDetail({ r, onBack, onWrite, onOpenJob }: { r: BookingRequest; onBack: () => void; onWrite: () => void; onOpenJob: (href: string) => void }) {
  const t = useT();
  const d = padDetail(r);
  const [shut, setShut] = useState<Record<string, true>>({});
  const [menu, setMenu] = useState(false);
  const [draft, setDraft] = useState('');
  const [more, setMore] = useState(false);
  const open = (id: string) => !shut[id];
  const toggle = (id: string) =>
    setShut((x) => {
      const n = { ...x };
      if (n[id]) delete n[id];
      else n[id] = true;
      return n;
    });
  const left = d.todo.filter((x) => !x.done).length;
  const todos = more ? d.todo : d.todo.slice(0, 4);

  const End = ({ e, first }: { e: typeof d.a; first?: boolean }) => (
    <div className={s.end}>
      <div className={s.endLab}>{e.lab}</div>
      <div className={s.rail}>
        <span className={s.railDot} aria-hidden="true">
          <IconShip size={16} stroke={1.9} />
        </span>
        {first && <span className={s.railLine} aria-hidden="true" />}
      </div>
      <div className={s.endPort}>
        <span className={s.endCode}>{e.code}</span>
        {e.port}
      </div>
      <div className={`${s.endDate} ${e.was ? s.endChg : ''}`} title={e.tip}>
        <span>
          <b>{e.date}</b> <span className={s.tx2}>({e.dk})</span>
        </span>
        {e.was && <span className={`${s.sm} ${s.tx2}`}>{e.was}</span>}
      </div>
    </div>
  );

  return (
    <>
      <PageHeader
        title={d.title}
        lead={
          <button type="button" className={s.back} aria-label="Quay lại danh sách RQ" title="Quay lại danh sách RQ" onClick={onBack}>
            <IconChevronLeft size={18} stroke={2} aria-hidden="true" />
          </button>
        }
        meta={d.chips.map((c) => (
          <Chip key={c.k} dot={c.dot} label={c.tip}>
            {c.k}
          </Chip>
        ))}
        sub={d.sub}
        noFind
        actions={
          <div className={s.acts}>
            <Button variant="ghost" icon={<IconDots size={18} aria-hidden="true" />} aria-label="Thêm thao tác" aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu((v) => !v)} />
            <Button variant="primary" className={s.b15} icon={<IconCalendarCheck size={16} aria-hidden="true" />} onClick={onWrite}>
              {d.primary}
            </Button>
            {menu && (
              <div role="menu" aria-label="Thao tác khác" className={s.menu}>
                <button type="button" role="menuitem" className={s.mi} onClick={onWrite}>
                  {d.second}
                </button>
                {MENU.map((m) => (
                  <div key={m.k}>
                    {m.g && <div className={s.mg}>{m.g}</div>}
                    <button type="button" role="menuitem" className={s.mi} title={m.tip} onClick={onWrite}>
                      {m.k}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        }
      />
      <div className={s.page}>
        <div className={s.grid}>
          <div className={s.col}>
            <Card id="plan" icon={IconMapRoute} title="Kế hoạch" tip="Theo lịch tàu / booking · ETD đổi thì ô tô vàng + “trước là …” · ETA = ETD + transit theo lịch tàu" open={open('plan')} onToggle={toggle}>
              <div className={s.stack}>
                <div className={s.vrow}>
                  <IconShip size={15} stroke={1.9} aria-hidden="true" className={s.tx2} />
                  <b className={s.fw}>
                    <button type="button" className={s.vlink} title="Hồ sơ tàu" onClick={onWrite}>
                      {d.vessel}
                    </button>
                  </b>
                  <span className={s.lb}>{d.vesselMeta}</span>
                  {d.hasChange && (
                    <span className={s.warnPill} title={d.changeTip}>
                      {t("bkx.planChanged")}
                    </span>
                  )}
                </div>
                <div className={s.ends}>
                  <End e={d.a} first />
                  <End e={d.b} />
                  <div className={s.transit} title={d.transitTip}>
                    <b>Transit</b>
                    <span className={s.transitV}>{d.transit}</span>
                    <span className={`${s.sm} ${s.lb}`}>
                      <IconAnchor size={14} stroke={1.8} aria-hidden="true" /> {t("bkx.portToPort")}
                    </span>
                  </div>
                </div>
                <div role="list" aria-label="Mốc và cut-off" className={s.chips}>
                  {d.planChips.map((c) => (
                    <span key={c.k} role="listitem" title={c.tip} className={`${s.pchip} ${TONE_TX[c.tone]}`}>
                      <span className={s.pdot} style={{ background: c.dot }} />
                      {c.k}
                    </span>
                  ))}
                </div>
              </div>
            </Card>
            <Card id="facts" icon={IconBoxSeam} title="Booking hãng" tip="Số booking, tàu / chuyến, cut-off · ô viền vàng = Ask HANA đọc từ thư hãng, cần soát lại" open={open('facts')} onToggle={toggle}>
              {d.ai && <div className={s.ai}>Ask HANA đọc số booking từ mail xác nhận của {d.carrier} lúc 09:12 · ô viền vàng cần kiểm lại</div>}
              <div className={s.facts}>
                {d.facts.map((f) => (
                  <div key={f.k} className={`${s.fact} ${f.warn ? s.factWarn : ''}`}>
                    <div className={`${s.sm} ${s.lb}`}>{f.k}</div>
                    <div className={`${s.fw} ${s.ell}`}>{f.v}</div>
                  </div>
                ))}
              </div>
            </Card>
            <Card id="cmt" icon={IconMessageCircle} title="Comment nội bộ" tip="Ghi chú cho Team BKG / Sale · gõ @ để nhắc tên" open={open('cmt')} onToggle={toggle}>
              <div className={s.cmtIn}>
                <input aria-label="Viết comment cho RQ" placeholder="Ghi chú cho Team BKG / Sale…" value={draft} onChange={(e) => setDraft(e.target.value)} />
                <Button className={s.b15} icon={<IconSend size={16} aria-hidden="true" />} onClick={onWrite}>
                  {t("bkx.send")}
                </Button>
              </div>
            </Card>
            <Card id="log" icon={IconClockHour4} title="Nhật ký job" tip="TblJobEventLog · tạo RQ, nhận, nhập booking, gửi xác nhận" open={open('log')} onToggle={toggle}>
              {d.log.map((l) => (
                <div key={l.k} className={s.log}>
                  <span className={s.ldot} style={{ background: l.dot }} />
                  <div className={s.grow}>{l.k}</div>
                  <span className={s.lb}>{l.when}</span>
                </div>
              ))}
            </Card>
          </div>
          <aside aria-label="Tóm tắt bên phải" className={s.col}>
            <Card id="todo" icon={IconChecklist} title="Việc cần làm" tip="Việc còn mở của bản ghi này · tối đa 4 việc, còn lại ở “Xem thêm”" count={left} open={open('todo')} onToggle={toggle}>
              <div role="list" aria-label="Việc cần làm">
                {todos.map((k, i) => (
                  <div key={k.k} role="listitem" className={s.todo} style={i ? undefined : { borderTop: 0 }}>
                    <span className={`${s.box} ${k.done ? s.boxOn : ''}`} aria-label={k.done ? 'Đã xong' : 'Chưa xong'} />
                    <div className={s.grow}>
                      <span className={`${s.fw500} ${s.ell} ${k.done ? s.strike : ''}`}>{k.k}</span>
                      <span className={`${s.sm} ${s.lb} ${s.who}`}>
                        <Avatar name={k.who} size={18} />
                        <span>{k.who}</span>
                        <span className={`${s.fw} ${TONE_TX[k.tone]}`}>{k.due}</span>
                      </span>
                    </div>
                    {k.act && !k.done && (
                      <Button size="sm" className={s.b14} onClick={onWrite}>
                        {k.act}
                      </Button>
                    )}
                  </div>
                ))}
                {!d.todo.length && <div className={`${s.lb} ${s.none}`}>{t("bkx.noOpenTodo")}</div>}
                {d.todo.length > 4 && (
                  <button type="button" className={s.moreBtn} onClick={() => setMore((v) => !v)}>
                    {more ? 'Thu gọn' : `Xem thêm ${d.todo.length - 4}`}
                  </button>
                )}
              </div>
            </Card>
            <Card id="info" icon={IconAlertCircle} title="Thông tin chính" tip="Mã và trường chính · bấm biểu tượng để sao chép" open={open('info')} onToggle={toggle}>
              <dl className={s.dl}>
                {d.info.map((f) => (
                  <div key={f.k} className={s.dr}>
                    <dt>{f.k}</dt>
                    <dd title={f.v} style={f.bold ? { fontWeight: 'var(--fw-label)' } : undefined}>
                      {f.href && f.v ? (
                        <a className={s.alink} href={f.href} onClick={(e) => { e.preventDefault(); onOpenJob(f.href!); }}>
                          {f.v} ↗
                        </a>
                      ) : (
                        f.v || '—'
                      )}
                    </dd>
                    {f.copy && f.v && (
                      <Button variant="ghost" icon={<IconCopy size={16} aria-hidden="true" />} aria-label={`Sao chép ${f.k}`} title="Sao chép" onClick={() => void navigator.clipboard?.writeText(f.v)} />
                    )}
                  </div>
                ))}
              </dl>
            </Card>
            <Card id="pic" icon={IconUsers} title="Người phụ trách" tip="Hai nguồn, gộp và bỏ trùng: (1) theo khách: Sale, CS, Sale Asst, Kế toán ở hồ sơ khách, job chép sang dòng booking; (2) nhận xử lý / được giao: người thực hiện trên chính bản ghi theo mô tả công việc. Chưa có người hiện “Chưa giao”." open={open('pic')} onToggle={toggle}>
              <div role="list" aria-label="Người phụ trách">
                {d.pics.map((p, i) => (
                  <div key={p.who + p.role} role="listitem" className={s.pic} title={p.role} style={i ? undefined : { borderTop: 0 }}>
                    <Avatar name={p.none ? '—' : p.who} size={32} />
                    <div className={s.grow}>
                      <div className={`${s.fw} ${s.ell} ${p.none ? s.lb : ''}`}>{p.who}</div>
                      <div className={`${s.sm} ${s.lb} ${s.ell}`}>{p.role}</div>
                    </div>
                    {p.none && (
                      <Button size="sm" className={s.b14} aria-label={`Giao ${p.role}`} onClick={onWrite}>
                        Giao
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </Card>
            <Card id="links" icon={IconLink} title="Liên kết" tip="Mở bản ghi liên quan trong HANA Ezy" open={open('links')} onToggle={toggle}>
              <div className={s.links}>
                {d.links.map((l) => (
                  <a key={l.k} className={s.lk} href={l.href} title={l.tip || undefined} onClick={(e) => { e.preventDefault(); onOpenJob(l.href); }}>
                    <IconExternalLink size={16} stroke={1.8} aria-hidden="true" className={s.lkIc} />
                    <span className={`${s.grow} ${s.fw} ${s.ell}`}>{l.k}</span>
                    <span className={`${s.sm} ${s.lb}`}>{l.sub} ↗</span>
                  </a>
                ))}
              </div>
            </Card>
          </aside>
        </div>
      </div>
    </>
  );
}
